import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../api';

const PCOIChart = ({ ticker = 'SPY' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/api/pcoi/${ticker}?days=${days}`)
      .then(res => {
        const history = (res.data.history || [])
          .filter(d => d.total_put_oi > 500000 || d.total_call_oi > 500000) // Filter out weekend/partial data
          .reverse()
          .map(d => ({
            ...d,
            date: d.date,
            shortDate: d.date.slice(5),
            putOiM: +(d.total_put_oi / 1000000).toFixed(2),
            callOiM: +(d.total_call_oi / 1000000).toFixed(2),
            pcRatio: +d.pc_ratio,
            spotPrice: +d.spot_price,
          }));
        setData(history);
        setLoading(false);
      })
      .catch(() => {
        setError('No P/C OI data available');
        setLoading(false);
      });
  }, [ticker, days]);

  if (loading) {
    return (
      <div style={{ padding: '30px 20px', textAlign: 'center', color: '#475569', fontSize: '13px' }}>
        <div style={{
          width: '20px', height: '20px', border: '2px solid #1e293b',
          borderTop: '2px solid #f59e0b', borderRadius: '50%',
          animation: 'spin 1s linear infinite', margin: '0 auto 8px',
        }} />
        Loading P/C OI...
      </div>
    );
  }

  if (error || data.length < 2) {
    return null; // Hide section if no data — don't show empty state
  }

  const latest = data[data.length - 1];
  const oldest = data[0];
  const ratioChange = latest && oldest ? (latest.pcRatio - oldest.pcRatio).toFixed(3) : 0;
  const isRising = ratioChange > 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div style={{
        background: 'rgba(3,7,18,0.95)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '10px', padding: '12px 16px', backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)', minWidth: '190px', fontSize: '12px',
      }}>
        <div style={{ fontWeight: 700, fontSize: '13px', color: '#f1f5f9', marginBottom: '8px' }}>{d.date}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#ef4444' }}>Put OI</span>
            <span style={{ color: '#ef4444', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{d.putOiM.toFixed(2)}M</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#22c55e' }}>Call OI</span>
            <span style={{ color: '#22c55e', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{d.callOiM.toFixed(2)}M</span>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '6px', marginTop: '4px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#f59e0b', fontWeight: 700 }}>P/C Ratio</span>
            <span style={{ color: '#f59e0b', fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{d.pcRatio.toFixed(3)}</span>
          </div>
          {d.spotPrice > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>Spot</span>
              <span style={{ color: '#f1f5f9', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>${d.spotPrice.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header row */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '14px', flexWrap: 'wrap', gap: '10px',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: '26px', fontWeight: 900, color: '#f59e0b',
            }}>
              {latest?.pcRatio.toFixed(2)}
            </span>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', fontWeight: 700,
              color: isRising ? '#ef4444' : '#22c55e',
              background: isRising ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
              padding: '2px 8px', borderRadius: '6px',
            }}>
              {ratioChange > 0 ? '+' : ''}{ratioChange}
            </span>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)' }}>vs {days}d ago</span>
          </div>
          <div style={{ display: 'flex', gap: '14px', fontSize: '11px' }}>
            <span style={{ color: '#ef4444' }}>
              Put: <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{latest?.putOiM.toFixed(1)}M</span>
            </span>
            <span style={{ color: '#22c55e' }}>
              Call: <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{latest?.callOiM.toFixed(1)}M</span>
            </span>
          </div>
        </div>

        {/* Time range */}
        <div style={{
          display: 'inline-flex', gap: '0',
          background: 'rgba(255,255,255,0.03)', borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.06)', padding: '3px',
        }}>
          {[30, 60, 90, 130].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              background: days === d ? 'rgba(245,158,11,0.15)' : 'transparent',
              color: days === d ? '#f59e0b' : '#64748b',
              border: 'none', borderRadius: '6px',
              padding: '5px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
            }}>
              {d === 130 ? '6M' : `${d}D`}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="putGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.12} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
            <XAxis
              dataKey="shortDate" tick={{ fill: '#475569', fontSize: 9 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false}
              interval={Math.max(0, Math.floor(data.length / 8))}
            />
            <YAxis
              yAxisId="oi" tick={{ fill: '#475569', fontSize: 9 }}
              axisLine={false} tickLine={false}
              tickFormatter={v => `${v.toFixed(1)}M`} width={50}
            />
            <YAxis
              yAxisId="ratio" orientation="right"
              tick={{ fill: '#f59e0b', fontSize: 9 }}
              axisLine={false} tickLine={false}
              tickFormatter={v => v.toFixed(1)} width={35}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.06)' }} />
            <Area yAxisId="oi" type="monotone" dataKey="putOiM" fill="url(#putGrad)" stroke="#ef4444" strokeWidth={1.5} strokeOpacity={0.5} />
            <Area yAxisId="oi" type="monotone" dataKey="callOiM" fill="url(#callGrad)" stroke="#22c55e" strokeWidth={1.5} strokeOpacity={0.5} />
            <Line yAxisId="ratio" type="natural" dataKey="pcRatio" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend + context */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '16px', fontSize: '10px', color: '#475569' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '12px', height: '2px', background: '#ef4444', display: 'inline-block', opacity: 0.6 }} />
            Put OI
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '12px', height: '2px', background: '#22c55e', display: 'inline-block', opacity: 0.6 }} />
            Call OI
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '12px', height: '2px', background: '#f59e0b', display: 'inline-block' }} />
            P/C Ratio
          </span>
        </div>
        <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.12)', fontStyle: 'italic' }}>
          P/C {'>'} 1.5 = heavy put hedge · P/C {'<'} 1.0 = call-dominant
        </span>
      </div>
    </div>
  );
};

export default PCOIChart;
